import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FJ180, FJ181 } from '../../services/j209Api';
import { useJ209Mode } from './hooks/useJ209Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useJ209Record } from './hooks/useJ209Record';
import { useJ209Navigation } from './hooks/useJ209Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1Header from './components/Tab1Header';
import DetailGrid from './components/DetailGrid';
import DetailMemo from './components/DetailMemo';
import ActionBar from './components/ActionBar';
import { getStatusBadge } from './helpers';
import './J209Form.css';

const J209Form: React.FC = () => {
  const navigate = useNavigate();
  const perms = useModulePermission(Modules.J209);
  const { mode, setMode, isEditing } = useJ209Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
  } = useJ209Record();

  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useJ209Navigation(loadRecordWithNames);

  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load J209 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx]);

  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: FJ180) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fj180_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除繳庫單 ${record.fj180_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    message.info('列印功能待實作');
  }, [record, isEditing]);

  // Update selected detail memo fields
  const handleDetailMemoUpdate = useCallback((key: keyof FJ181, value: FJ181[keyof FJ181]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow, setDetails]);

  const statusBadge = getStatusBadge(record);

  return (
    <PermissionGuard module={Modules.J209} action="query">
    <div className="j209-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>研發管理</a>
              <span>/</span>
              <span className="current">研發庫存繳庫單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>J209-研發庫存繳庫單</h1>
            <span className="badge badge-default">J209</span>
            {statusBadge && (
              <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>
            )}
          </div>
        </div>

        <div className="search-bar">
          <label>快速查詢</label>
          <input
            type="text"
            placeholder="輸入繳庫單號"
            disabled={isEditing}
            style={{ width: 160 }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                const val = (e.target as HTMLInputElement).value.trim();
                if (val) {
                  await loadRecordWithNames(val);
                  const idx = allIds.indexOf(val);
                  if (idx >= 0) setCurrentIdx(idx);
                }
              }
            }}
          />
          <span style={{ fontSize: 12, color: '#999' }}>
            {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '無資料'}
          </span>
        </div>

        <div className="content-panel">
          <Tab1Header
            formData={formData}
            names={names}
            isEditing={isEditing}
            getField={getField}
            setField={setField}
            resolveSingleName={resolveSingleName}
          />
        </div>

        <div className="content-panel">
          <DetailGrid
            details={details}
            selectedRow={selectedRow}
            isEditing={isEditing}
            setSelectedRow={setSelectedRow}
            setDetails={setDetails}
            addDetailRow={addDetailRow}
            deleteDetailRow={deleteDetailRow}
          />
          <DetailMemo
            detail={details[selectedRow]}
            isEditing={isEditing}
            onUpdate={handleDetailMemoUpdate}
          />
        </div>

        <ActionBar
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onPrint={handlePrint}
          onNavTo={handleNavTo}
          onExit={() => navigate('/erp')}
        />
      </div>
    </div>
    </PermissionGuard>
  );
};

export default J209Form;
